import React from 'react';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
// path sende neredeyse ona göre ayarla
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import { GreenSwitch } from '../modal/detail/ui/GreenSwitch';

function KpiPill({ icon, label, value, tone = 'neutral' }) {
  const theme = useMuiTheme();

  const toneMap = {
    primary: {
      bg: 'rgba(26,115,232,.08)',
      border: 'rgba(26,115,232,.28)',
      icon: theme.palette.info.main,
    },
    success: {
      bg: 'rgba(76,175,80,.08)',
      border: 'rgba(76,175,80,.28)',
      icon: theme.palette.success.main,
    },
    error: {
      bg: 'rgba(244,67,54,.07)',
      border: 'rgba(244,67,54,.26)',
      icon: theme.palette.error.main,
    },
    neutral: {
      bg: theme.palette.background.default,
      border: theme.palette.divider,
      icon: theme.palette.text.secondary,
    },
  };

  const t = toneMap[tone] || toneMap.neutral;

  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1 },
        px: { xs: 0.9, sm: 1.2 },
        py: { xs: 0.55, sm: 0.7 },
        borderRadius: { xs: '12px', sm: '14px' },
        border: `1px solid ${t.border}`,
        background: t.bg,
        minWidth: 0,
      }}
    >
      <MDBox
        sx={{
          width: { xs: 24, sm: 28 },
          height: { xs: 24, sm: 28 },
          borderRadius: { xs: '8px', sm: '10px' },
          display: 'grid',
          placeItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: { xs: 16, sm: 18 }, color: t.icon }}>{icon}</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          variant="caption"
          sx={{ opacity: 0.72, lineHeight: 1.1, fontSize: { xs: '0.70rem', sm: '0.75rem' } }}
        >
          {label}
        </MDTypography>

        <MDTypography
          variant="h6"
          fontWeight="bold"
          sx={{ lineHeight: 1.2, mt: 0.1, fontSize: { xs: '1.0rem', sm: '1.15rem' } }}
        >
          {value}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

export function StaffToolbar({
  q,
  onQ,
  onlyActive,
  onOnlyActive,
  count,
  totalCount,
  activeCount,
  passiveCount,
  onCreate,
}) {
  const theme = useMuiTheme();

  return (
    <Card
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
      }}
    >
      {/* header */}
      <MDBox
        sx={{
          px: { xs: 1, sm: 1.7 },
          py: { xs: 0.55, sm: 0.8 },

          background: 'linear-gradient(180deg, rgba(26,115,232,.16), rgba(26,115,232,.04))',
        }}
      >
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={0.7}
          sx={{ flexWrap: 'wrap' }}
        >
          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              Eğitmenler
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* KPI pills */}
        <MDBox
          sx={{
            mt: 0.45,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))', // ✅ mobilde yan yana 2
              sm: 'repeat(3, minmax(0, 1fr))', // ✅ tablet+ 3
            },
            gap: { xs: 0.6, sm: 0.9 },
          }}
        >
          <KpiPill icon="groups" label="Toplam" value={totalCount} tone="primary" />
          <KpiPill icon="check_circle" label="Aktif" value={activeCount} tone="success" />
          <KpiPill icon="cancel" label="Pasif" value={passiveCount} tone="error" />
        </MDBox>
      </MDBox>

      <Divider sx={{ my: 0 }} />

      {/* toolbar */}
      <MDBox
        sx={{
          pt: { xs: 0.2, sm: 0.3 }, // 🔻 üstten daha yakın
          pb: { xs: 0.7, sm: 0.9 },
          px: { xs: 0.7, sm: 1 },
          display: 'grid',
          gap: 0,
        }}
      >
        <MDInput
          value={q}
          onChange={(e) => onQ?.(e.target.value)}
          placeholder="Ara: isim, telefon, e-posta…"
          InputProps={{
            startAdornment: (
              <MDBox sx={{ display: 'flex', alignItems: 'center', pr: 0.75 }}>
                <Icon sx={{ opacity: 0.65, fontSize: 18 }}>search</Icon>
              </MDBox>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: 36, // 🔻 ana yükseklik
              paddingTop: 0,
              paddingBottom: 0,
            },
            '& .MuiOutlinedInput-input': {
              padding: '6px 10px', // 🔻 input iç padding
              fontSize: '0.85rem', // 🔻 yazı biraz daha sıkı
            },
          }}
        />

        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ flexWrap: 'wrap' }}
        >
          <MDBox display="flex" gap={1} sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
              }}
              label={`Görünen: ${count}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={onlyActive ? 'Aktif Eğitmenler' : 'Tümü'}
              variant="outlined"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                borderColor: onlyActive ? 'rgba(26,115,232,.35)' : undefined,
                background: onlyActive ? 'rgba(0, 0, 0, 0.06)' : undefined,
              }}
            />
          </MDBox>

          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <GreenSwitch
                checked={!!onlyActive}
                onChange={(e) => onOnlyActive?.(e.target.checked)}
              />
            }
            label={
              <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Aktif Eğitmenler
              </MDTypography>
            }
          />
        </MDBox>
      </MDBox>
    </Card>
  );
}
